import type React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { GroupedAreas, Area } from "@/types/area"

interface SchoolSearchProps {
  selectedAreas: string[]
  setSelectedAreas: React.Dispatch<React.SetStateAction<string[]>>
  area: string
  handleAreaChange: (value: string) => void
  handleAddArea: (newArea: Area) => void
  handleAddMultipleAreas: (areas: Area[]) => void
  isDropdownOpen: boolean
  setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  groupedAreas: GroupedAreas
  schoolType: string
  setSchoolType: (type: string) => void
  schoolLevel: string[]
  setSchoolLevel: (level: string[]) => void
  academicPerformance: string
  setAcademicPerformance: (performance: string) => void
  inputErrors: { [key: string]: string }
  handleSubmit: (e: React.FormEvent) => Promise<void>
  isLoading: boolean
  resetAllCriteria: () => void
  isAreaPage: boolean
  initialAreas: string[]
}

const SchoolSearch: React.FC<SchoolSearchProps> = ({
  selectedAreas,
  setSelectedAreas,
  area,
  handleAreaChange,
  handleAddArea,
  handleAddMultipleAreas,
  isDropdownOpen,
  setIsDropdownOpen,
  groupedAreas,
  schoolType,
  setSchoolType,
  schoolLevel,
  setSchoolLevel,
  academicPerformance,
  setAcademicPerformance,
  inputErrors,
  handleSubmit,
  isLoading,
  resetAllCriteria,
}) => {

  const handleRemoveArea = (areaToRemove: string) => {
    setSelectedAreas(selectedAreas.filter((a) => a !== areaToRemove))
  }

  const handleAddKommun = (kommun: string, areas: Area[]) => {
    handleAddMultipleAreas(areas)
    setIsDropdownOpen(false)
  }

  const handleAreaClick = (area: Area) => {
    handleAddArea(area)
    setIsDropdownOpen(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full max-w-[800px]">
      <div className="space-y-2">
        {selectedAreas.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 md:pl-[140px]">
            {selectedAreas.map((selectedArea) => (
              <div
                key={selectedArea}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center"
              >
                {selectedArea}
                <button
                  type="button"
                  onClick={() => handleRemoveArea(selectedArea)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-[140px_1fr] gap-2 md:gap-4 items-start">
          <Label htmlFor="area-search" className="md:text-right md:pt-2">
            Välj område (en eller flera)
          </Label>
          <div className="w-full">
            <div className="relative">
              <Input
                type="text"
                id="area-search"
                placeholder="Skriv ett område, tex Vasastan..."
                value={area}

                onChange={(e) => {
                  handleAreaChange(e.target.value)
                  // Force focus and dropdown open when typing
                  if (e.target.value.length > 2) {
                    e.target.focus()
                    setIsDropdownOpen(true)
                  }
                }}
                onFocus={() => {
                  setIsDropdownOpen(true)
                }}
                onBlur={(e) => {
                  // Check if the related target is within the dropdown
                  const relatedTarget = e.relatedTarget as HTMLElement
                  const isClickingDropdown = relatedTarget?.closest('.dropdown-container')
                  
                  if (!isClickingDropdown) {
                    setTimeout(() => {
                      setIsDropdownOpen(false)
                    }, 200)
                  }
                }}
              />
              {isDropdownOpen && Object.keys(groupedAreas).length > 0 && (
                <ul className="dropdown-container absolute z-10 w-full bg-white border border-gray-300 mt-1 max-h-60 overflow-auto rounded-md shadow-lg">
                  {Object.entries(groupedAreas).map(([kommun, areas]) => (
                    <li key={kommun} className="px-2 py-1">
                      <div
                        className="font-bold text-gray-700 cursor-pointer hover:text-blue-600"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleAddKommun(kommun, areas)
                        }}
                      >
                        {kommun}
                      </div>
                      <ul>
                        {areas.map((area) => (
                          <li
                            key={`${kommun}-${area.name}`}
                            className="px-4 py-1 hover:bg-gray-100 cursor-pointer"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              handleAreaClick(area)
                            }}
                          >
                            {area.name}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1 italic">
              Vi täcker just nu områden i Stockholms län, Göteborg, Malmö, Uppsala och Lund
            </p>
          </div>
        </div>

        {inputErrors.area && <p className="text-red-500 text-sm md:pl-[140px]">{inputErrors.area}</p>}
      </div>

      <div className="grid md:grid-cols-[140px_1fr] gap-2 md:gap-4 items-start md:items-center">
        <Label className="md:text-right">Skoltyp</Label>
        <div className="flex flex-wrap gap-2 md:gap-4 w-full">
          {[
            { value: "private", label: "Friskola" },
            { value: "public", label: "Kommunal" },
            { value: "all", label: "Både och" },
          ].map((type) => (
            <Button
              key={type.value}
              type="button"
              variant={schoolType === type.value ? "default" : "outline"}
              onClick={() => setSchoolType(type.value)}
              className={`${
                schoolType === type.value ? "bg-[#2D4191] text-white" : "bg-white text-[#2D4191] border-[#2D4191]"
              } hover:bg-[#2D4191]/90 hover:text-white flex-1 md:flex-none md:w-[140px]`}
            >
              {type.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-[140px_1fr] gap-2 md:gap-4 items-start md:items-center">
        <Label className="md:text-right">
          <span className="block sm:hidden">Låg/Mellan/Högstadiet</span>
          <span className="hidden sm:block">Årskurser</span>
        </Label>
        <div className="flex flex-wrap gap-2 md:gap-4 w-full">
          {[
            { value: "lågstadiet", label: "Lågstadiet", shortLabel: "Låg" },
            { value: "mellanstadiet", label: "Mellanstadiet", shortLabel: "Mellan" },
            { value: "högstadiet", label: "Högstadiet", shortLabel: "Hög" },
          ].map((level) => (
            <Button
              key={level.value}
              type="button"
              variant={schoolLevel.includes(level.value) ? "default" : "outline"}
              onClick={() => {
                const newLevels = schoolLevel.includes(level.value)
                  ? schoolLevel.filter((l) => l !== level.value)
                  : [...schoolLevel, level.value]
                setSchoolLevel(newLevels)
              }}
              className={`${
                schoolLevel.includes(level.value)
                  ? "bg-[#2D4191] text-white"
                  : "bg-white text-[#2D4191] border-[#2D4191]"
              } hover:bg-[#2D4191]/90 hover:text-white flex-1 md:flex-none md:w-[140px] text-xs sm:text-sm`}
            >
              <span className="block sm:hidden">{level.shortLabel}</span>
              <span className="hidden sm:block">{level.label}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-[140px_1fr] gap-2 md:gap-4 items-start md:items-center">
        <Label className="md:text-right">Akademiska resultat</Label>
        <div className="flex flex-wrap gap-2 md:gap-4 w-full">
          {[
            { value: "all", label: "Spelar ingen roll", shortLabel: "Alla" },
            { value: "above_average", label: "Över genomsnittet", shortLabel: "Över snittet" },
            { value: "top_25", label: "Top 25%", shortLabel: "Top 25%" },
          ].map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={academicPerformance === option.value ? "default" : "outline"}
              onClick={() => setAcademicPerformance(option.value)}
              className={`${
                academicPerformance === option.value
                  ? "bg-[#2D4191] text-white"
                  : "bg-white text-[#2D4191] border-[#2D4191]"
              } hover:bg-[#2D4191]/90 hover:text-white flex-1 md:flex-none md:w-[140px] text-xs sm:text-sm`}
            >
              <span className="block sm:hidden">{option.shortLabel}</span>
              <span className="hidden sm:block">{option.label}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-[140px_1fr] gap-2 md:gap-4 items-start md:items-center">
        <div className="md:col-start-2 flex flex-col sm:flex-row gap-2 md:gap-4 w-full">
          <Button
            type="submit"
            disabled={isLoading}
            className="bg-green-600 text-white hover:bg-green-700 w-full sm:w-auto sm:flex-grow md:w-[300px]"
          >
            {isLoading ? "Söker..." : "Hitta skolor"}
          </Button>
          <Button
            type="button"
            onClick={resetAllCriteria}
            className="bg-white text-[#2D4191] border border-[#2D4191] hover:bg-gray-100 w-full sm:w-auto md:w-[140px]"
          >
            Återställ
          </Button>
        </div>
      </div>
    </form>
  )
}

export default SchoolSearch

